import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import AdminLayout from "../../components/admin/AdminLayout";
import { api, formatApiError } from "../../lib/api";
import { ChevronLeft, X, Plus, Upload } from "lucide-react";
import { toast } from "sonner";
import RichTextEditor from "../../components/admin/RichTextEditor";

const EMPTY = {
  sku: "", name: "", description: "", oem_number: "",
  category: "side-mirror", position: "left", fitment_type: "oem-style",
  price: 0, inventory_qty: 0, images: [], price_tiers: [],
  features: { heated: false, blind_spot: false, auto_dimming: false, with_camera: false, hud: false, rain_sensor: false, with_backing_plate: false },
  vehicle_ids: [],
  shipping_class_id: "", weight_lbs: 0, length_in: "", width_in: "", height_in: "",
  oversized: false, freight_required: false, ships_free: false, local_pickup_eligible: false,
};

const CATEGORY_OPTIONS = [
  { value: "side-mirror", label: "Side Mirror" },
  { value: "windshield", label: "Windshield" },
  { value: "backglass", label: "Backglass" },
  { value: "sunroof", label: "Sunroof" },
];

// Positions are intentionally a single flat list so the admin can always pick
// any value, but UX-helpful: defaults per category are applied when the
// category changes.
const POSITION_OPTIONS = [
  { value: "left", label: "Left", categories: ["side-mirror"] },
  { value: "right", label: "Right", categories: ["side-mirror"] },
  { value: "pair", label: "Pair", categories: ["side-mirror"] },
  { value: "front", label: "Front", categories: ["windshield", "backglass", "sunroof"] },
  { value: "rear", label: "Rear", categories: ["windshield", "backglass", "sunroof"] },
  { value: "panoramic", label: "Panoramic", categories: ["sunroof"] },
  { value: "upper-left", label: "Upper left", categories: ["side-mirror"] },
  { value: "upper-right", label: "Upper right", categories: ["side-mirror"] },
  { value: "lower-left", label: "Lower left", categories: ["side-mirror"] },
  { value: "lower-right", label: "Lower right", categories: ["side-mirror"] },
  { value: "n/a", label: "N/A", categories: ["side-mirror", "windshield", "backglass", "sunroof"] },
];

const FEATURE_OPTIONS = [
  { k: "heated", l: "Heated" },
  { k: "blind_spot", l: "Blind spot" },
  { k: "auto_dimming", l: "Auto-dimming" },
  { k: "with_camera", l: "With camera" },
  { k: "hud", l: "HUD — Heads Up Display" },
  { k: "rain_sensor", l: "Rain sensor" },
  { k: "with_backing_plate", l: "w/BackingPlate" },
];

const ACCEPTED_IMAGE_TYPES = "image/jpeg,image/png,image/webp";
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export default function AdminProductForm() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const location = useLocation();

  // "Sell Similar" — pre-fill a brand-new draft from another product. The
  // original product is never touched: we only copy field values + image
  // references into local form state, blank out the SKU, and let the admin
  // save it as a separate new product (new id + slug generated server-side).
  useEffect(() => {
    if (!isNew) return;
    const prefill = location.state?.prefill;
    if (!prefill) return;
    setForm((f) => ({ ...EMPTY, ...f, ...prefill, sku: "" }));
    if (prefill.vehicle_ids?.length) {
      api.get(`/admin/vehicles?limit=2000`).then(({ data }) => {
        const map = new Map(data.vehicles.map((v) => [v.id, v]));
        const attached = prefill.vehicle_ids
          .map((vid) => map.get(vid))
          .filter(Boolean)
          .map((v) => ({ id: v.id, label: `${v.year} ${v.make} ${v.model}${v.trim ? " · " + v.trim : ""}` }));
        setAttachedVehicles(attached);
      });
    }
    // eslint-disable-next-line
  }, []);

  const [form, setForm] = useState(EMPTY);
  const [imageInput, setImageInput] = useState("");
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Fitment picker state
  const [brands, setBrands] = useState([]);
  const [years, setYears] = useState([]);
  const [pickYear, setPickYear] = useState("");
  const [pickBrand, setPickBrand] = useState("");
  const [pickModel, setPickModel] = useState("");
  const [pickTrim, setPickTrim] = useState("");
  const [pickModels, setPickModels] = useState([]);
  const [pickTrims, setPickTrims] = useState([]);
  const [attachedVehicles, setAttachedVehicles] = useState([]); // [{id, label}]
  const [shipClasses, setShipClasses] = useState([]);

  useEffect(() => {
    api.get("/admin/shipping-classes").then(({ data }) => setShipClasses(data.classes || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/admin/brands").then(({ data }) => setBrands(data.brands));
    api.get("/vehicles/years").then(({ data }) => setYears(data.years));
  }, []);

  useEffect(() => {
    if (pickBrand) {
      api.get(`/admin/models?brand_id=${pickBrand}`).then(({ data }) => setPickModels(data.models));
    } else {
      setPickModels([]); setPickModel("");
    }
  }, [pickBrand]);

  useEffect(() => {
    if (pickModel) {
      api.get(`/admin/trims?model_id=${pickModel}`).then(({ data }) => setPickTrims(data.trims));
    } else {
      setPickTrims([]); setPickTrim("");
    }
  }, [pickModel]);

  useEffect(() => {
    if (isNew) return;
    api.get(`/admin/products?limit=1000`).then(async ({ data }) => {
      const p = data.products.find((x) => x.id === id);
      if (!p) {
        toast.error("Product not found");
        navigate("/admin/products");
        return;
      }
      setForm({
        sku: p.sku, name: p.name, description: p.description || "",
        oem_number: p.oem_number || "",
        category: p.category, position: p.position, fitment_type: p.fitment_type,
        price: p.price, inventory_qty: p.inventory_qty,
        images: p.images || [],
        price_tiers: p.price_tiers || [],
        features: p.features || EMPTY.features,
        vehicle_ids: p.vehicle_ids || [],
        shipping_class_id: p.shipping_class_id || "",
        weight_lbs: p.weight_lbs ?? 0,
        length_in: p.length_in ?? "", width_in: p.width_in ?? "", height_in: p.height_in ?? "",
        oversized: !!p.oversized, freight_required: !!p.freight_required,
        ships_free: !!p.ships_free, local_pickup_eligible: !!p.local_pickup_eligible,
      });
      // Hydrate attached vehicle labels
      if (p.vehicle_ids?.length) {
        const { data: vdata } = await api.get(`/admin/vehicles?limit=2000`);
        const map = new Map(vdata.vehicles.map((v) => [v.id, v]));
        const attached = p.vehicle_ids
          .map((vid) => map.get(vid))
          .filter(Boolean)
          .map((v) => ({ id: v.id, label: `${v.year} ${v.make} ${v.model}${v.trim ? " · " + v.trim : ""}` }));
        setAttachedVehicles(attached);
      }
      setLoading(false);
    });
    // eslint-disable-next-line
  }, [id]);

  const updateField = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    // When category changes, if the current position is no longer valid for
    // that category, reset to the first valid position for the new category.
    if (k === "category") {
      const valid = POSITION_OPTIONS.filter((p) => p.categories.includes(v)).map((p) => p.value);
      if (!valid.includes(next.position)) {
        next.position = valid[0] || "n/a";
      }
    }
    return next;
  });
  const updateFeature = (k, v) => setForm((f) => ({ ...f, features: { ...f.features, [k]: v } }));

  const addImage = () => {
    const url = imageInput.trim();
    if (!url) return;
    setForm((f) => ({ ...f, images: [...f.images, url] }));
    setImageInput("");
  };
  const removeImage = (i) => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));

  // Volume/tiered pricing handlers
  const addTier = () => setForm((f) => ({ ...f, price_tiers: [...(f.price_tiers || []), { min_qty: "", max_qty: "", unit_price: "" }] }));
  const updateTier = (i, key, val) => setForm((f) => {
    const t = [...(f.price_tiers || [])];
    t[i] = { ...t[i], [key]: val };
    return { ...f, price_tiers: t };
  });
  const removeTier = (i) => setForm((f) => ({ ...f, price_tiers: (f.price_tiers || []).filter((_, idx) => idx !== i) }));

  const handleFileSelect = async (filesList) => {
    const files = Array.from(filesList || []);
    if (!files.length) return;
    // Client-side pre-validation (server re-validates with magic bytes).
    const accepted = ["image/jpeg", "image/png", "image/webp"];
    for (const f of files) {
      if (!accepted.includes(f.type)) {
        toast.error(`"${f.name}" is not a supported image (JPG, PNG, WEBP only)`);
        return;
      }
      if (f.size > MAX_IMAGE_BYTES) {
        toast.error(`"${f.name}" exceeds the 5 MB limit`);
        return;
      }
    }
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/admin/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const urls = (data.files || []).map((x) => x.url);
      setForm((f) => ({ ...f, images: [...f.images, ...urls] }));
      toast.success(`Uploaded ${urls.length} image${urls.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const attachVehicle = async () => {
    if (!pickYear || !pickBrand || !pickModel) {
      toast.error("Pick year, make, and model");
      return;
    }
    try {
      const payload = {
        year: parseInt(pickYear),
        brand_id: pickBrand,
        model_id: pickModel,
        trim_id: pickTrim || null,
      };
      const { data: v } = await api.post("/admin/vehicles", payload);
      const brand = brands.find((b) => b.id === pickBrand);
      const model = pickModels.find((m) => m.id === pickModel);
      const trim = pickTrims.find((t) => t.id === pickTrim);
      const label = `${pickYear} ${brand?.name || "?"} ${model?.name || "?"}${trim ? " · " + trim.name : ""}`;
      if (attachedVehicles.find((a) => a.id === v.id)) {
        toast.info("Already attached");
        return;
      }
      setAttachedVehicles([...attachedVehicles, { id: v.id, label }]);
      setForm((f) => ({ ...f, vehicle_ids: [...new Set([...f.vehicle_ids, v.id])] }));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeVehicle = (vid) => {
    setAttachedVehicles((a) => a.filter((x) => x.id !== vid));
    setForm((f) => ({ ...f, vehicle_ids: f.vehicle_ids.filter((x) => x !== vid) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        inventory_qty: parseInt(form.inventory_qty),
        // Shipping (Phase S1) — coerce numbers; empty optional dims -> null.
        shipping_class_id: form.shipping_class_id || null,
        weight_lbs: form.weight_lbs === "" || form.weight_lbs == null ? 0 : parseFloat(form.weight_lbs),
        length_in: form.length_in === "" || form.length_in == null ? null : parseFloat(form.length_in),
        width_in: form.width_in === "" || form.width_in == null ? null : parseFloat(form.width_in),
        height_in: form.height_in === "" || form.height_in == null ? null : parseFloat(form.height_in),
        oversized: !!form.oversized,
        freight_required: !!form.freight_required,
        ships_free: !!form.ships_free,
        local_pickup_eligible: !!form.local_pickup_eligible,
        // Coerce empty optional string -> null so server stores absence
        // cleanly and the PDP "only show when set" check works.
        oem_number: (form.oem_number || "").trim() || null,
        // Normalize tier rows -> numbers; drop incomplete rows. Empty max = open-ended.
        price_tiers: (form.price_tiers || [])
          .filter((t) => String(t.min_qty) !== "" && String(t.unit_price) !== "")
          .map((t) => ({
            min_qty: parseInt(t.min_qty, 10),
            max_qty: (t.max_qty === "" || t.max_qty == null) ? null : parseInt(t.max_qty, 10),
            unit_price: parseFloat(t.unit_price),
          })),
      };
      if (isNew) {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      } else {
        await api.put(`/admin/products/${id}`, payload);
        toast.success("Saved");
      }
      navigate("/admin/products");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AdminLayout title="Product"><div className="text-zinc-500">Loading…</div></AdminLayout>;

  return (
    <AdminLayout title={isNew ? "New product" : "Edit product"}>
      <Link to="/admin/products" className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-900 mb-6">
        <ChevronLeft size={12} /> Back to products
      </Link>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="admin-product-form">
        {/* Main */}
        <div className="lg:col-span-2 space-y-6">
          <Card title="Basic info">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="SKU" required>
                <input data-testid="form-sku" value={form.sku} onChange={(e) => updateField("sku", e.target.value)} className={inputCls} required />
              </Field>
              <Field label="OEM / OEM Equivalent No.">
                <input data-testid="form-oem-number" value={form.oem_number} onChange={(e) => updateField("oem_number", e.target.value)} placeholder="e.g. 81940-11020 (optional)" className={inputCls} />
              </Field>
              <Field label="Price (USD)" required>
                <input data-testid="form-price" type="number" min={0} step="0.01" value={form.price} onChange={(e) => updateField("price", e.target.value)} className={inputCls} required />
              </Field>
              <div className="md:col-span-2">
                <Field label="Name" required>
                  <input data-testid="form-name" value={form.name} onChange={(e) => updateField("name", e.target.value)} className={inputCls} required />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Description">
                  <RichTextEditor value={form.description} onChange={(html) => updateField("description", html)} />
                </Field>
              </div>
            </div>
          </Card>

          <Card title="Classification">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Category" required>
                <select data-testid="form-category" value={form.category} onChange={(e) => updateField("category", e.target.value)} className={inputCls}>
                  {CATEGORY_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Position">
                <select data-testid="form-position" value={form.position} onChange={(e) => updateField("position", e.target.value)} className={inputCls}>
                  {POSITION_OPTIONS.filter((p) => p.categories.includes(form.category)).map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Fitment type">
                <select data-testid="form-fitment-type" value={form.fitment_type} onChange={(e) => updateField("fitment_type", e.target.value)} className={inputCls}>
                  <option value="oem-style">OEM-style</option>
                  <option value="aftermarket">Aftermarket</option>
                </select>
              </Field>
            </div>
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2 block">Features</div>
              <div className="flex flex-wrap gap-x-5 gap-y-3">
                {FEATURE_OPTIONS.map(({ k, l }) => (
                  <label key={k} className="flex items-center gap-2 text-sm">
                    <input data-testid={`form-feat-${k}`} type="checkbox" checked={!!form.features[k]} onChange={(e) => updateFeature(k, e.target.checked)} className="w-4 h-4" />
                    {l}
                  </label>
                ))}
              </div>
            </div>
          </Card>

          <Card title="Volume / tiered pricing (optional)">
            <p className="text-xs text-zinc-500 mb-3">
              Per-unit price for quantity ranges (e.g. 1 → $68.99, 2–4 → $64.99, 5+ → $59.99). Leave empty to use the single price for all quantities. Ranges must not overlap; only the last tier can be open-ended (blank max).
            </p>
            <div className="space-y-2">
              {(form.price_tiers || []).map((t, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`tier-row-${i}`}>
                  <input data-testid={`tier-min-${i}`} type="number" min={1} value={t.min_qty} onChange={(e) => updateTier(i, "min_qty", e.target.value)} placeholder="Min qty" className={inputCls + " w-24"} />
                  <span className="text-zinc-400 text-sm">to</span>
                  <input data-testid={`tier-max-${i}`} type="number" min={1} value={t.max_qty ?? ""} onChange={(e) => updateTier(i, "max_qty", e.target.value)} placeholder="Max (∞)" className={inputCls + " w-24"} />
                  <span className="text-zinc-400 text-sm">@ $</span>
                  <input data-testid={`tier-price-${i}`} type="number" min={0} step="0.01" value={t.unit_price} onChange={(e) => updateTier(i, "unit_price", e.target.value)} placeholder="Unit price" className={inputCls + " flex-1"} />
                  <button type="button" data-testid={`tier-remove-${i}`} onClick={() => removeTier(i)} className="p-2 text-zinc-500 hover:text-red-600"><X size={14} /></button>
                </div>
              ))}
            </div>
            <button type="button" data-testid="tier-add" onClick={addTier} className="mt-3 inline-flex items-center gap-2 text-sm px-3 h-9 border border-zinc-300 rounded-sm hover:bg-zinc-50">
              <Plus size={14} /> Add tier
            </button>
          </Card>

          <Card title="Product images">
            <div className="space-y-4">
              {/* Local file upload */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2">Upload from computer</div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ACCEPTED_IMAGE_TYPES}
                  data-testid="form-image-file-input"
                  onChange={(e) => handleFileSelect(e.target.files)}
                  className="hidden"
                />
                <button
                  type="button"
                  data-testid="form-image-upload-btn"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-900 text-white text-sm rounded-sm disabled:opacity-50"
                >
                  <Upload size={14} /> {uploading ? "Uploading…" : "Select images"}
                </button>
                <div className="text-[11px] text-zinc-500 mt-1">JPG, PNG, WEBP. Up to 5 MB per image.</div>
              </div>

              {/* URL paste */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2">Or paste an image URL</div>
                <div className="flex gap-2">
                  <input
                    data-testid="form-image-input"
                    value={imageInput}
                    onChange={(e) => setImageInput(e.target.value)}
                    placeholder="https://…"
                    className={inputCls}
                  />
                  <button type="button" data-testid="form-image-add" onClick={addImage} className="px-4 bg-zinc-900 text-white text-sm rounded-sm">Add</button>
                </div>
              </div>

              {/* Preview grid */}
              {form.images.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2">Attached ({form.images.length})</div>
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                    {form.images.map((src, i) => (
                      <div key={i} className="relative group">
                        <img src={src} alt="" className="w-full h-24 object-cover border border-zinc-200 rounded" />
                        <button type="button" onClick={() => removeImage(i)} data-testid={`form-image-remove-${i}`} className="absolute top-1 right-1 bg-white border border-zinc-200 rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card title="Inventory">
            <Field label="Stock quantity">
              <input data-testid="form-inventory" type="number" min={0} value={form.inventory_qty} onChange={(e) => updateField("inventory_qty", e.target.value)} className={inputCls} />
            </Field>
          </Card>

          <Card title="Shipping">
            <div className="space-y-3" data-testid="form-shipping">
              <Field label="Shipping class">
                <select data-testid="form-ship-class" value={form.shipping_class_id} onChange={(e) => updateField("shipping_class_id", e.target.value)} className={inputCls}>
                  <option value="">None</option>
                  {shipClasses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Weight (lb)">
                <input data-testid="form-weight" type="number" min={0} step="0.01" value={form.weight_lbs} onChange={(e) => updateField("weight_lbs", e.target.value)} className={inputCls} />
              </Field>
              <div className="grid grid-cols-3 gap-2">
                <Field label="Length (in)"><input type="number" min={0} step="0.1" value={form.length_in} onChange={(e) => updateField("length_in", e.target.value)} className={inputCls} /></Field>
                <Field label="Width (in)"><input type="number" min={0} step="0.1" value={form.width_in} onChange={(e) => updateField("width_in", e.target.value)} className={inputCls} /></Field>
                <Field label="Height (in)"><input type="number" min={0} step="0.1" value={form.height_in} onChange={(e) => updateField("height_in", e.target.value)} className={inputCls} /></Field>
              </div>
              <div className="space-y-2 pt-1">
                {[
                  { k: "oversized", label: "Oversized item" },
                  { k: "freight_required", label: "Freight required" },
                  { k: "ships_free", label: "Ships free" },
                  { k: "local_pickup_eligible", label: "Local pickup eligible" },
                ].map(({ k, label: lbl }) => (
                  <label key={k} className="flex items-center gap-2 text-sm text-zinc-700">
                    <input data-testid={`form-ship-${k}`} type="checkbox" checked={!!form[k]} onChange={(e) => updateField(k, e.target.checked)} className="w-4 h-4" />
                    {lbl}
                  </label>
                ))}
              </div>
            </div>
          </Card>

          <Card title="Vehicle fitment">
            <div className="grid grid-cols-2 gap-2 mb-3">
              <select data-testid="fit-year" value={pickYear} onChange={(e) => setPickYear(e.target.value)} className={inputCls}>
                <option value="">Year</option>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              <select data-testid="fit-brand" value={pickBrand} onChange={(e) => setPickBrand(e.target.value)} className={inputCls}>
                <option value="">Make</option>
                {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              <select data-testid="fit-model" value={pickModel} onChange={(e) => setPickModel(e.target.value)} disabled={!pickBrand} className={inputCls}>
                <option value="">Model</option>
                {pickModels.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              <select data-testid="fit-trim" value={pickTrim} onChange={(e) => setPickTrim(e.target.value)} disabled={!pickModel} className={inputCls}>
                <option value="">Any trim</option>
                {pickTrims.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <button type="button" data-testid="fit-attach" onClick={attachVehicle} className="w-full inline-flex items-center justify-center gap-2 bg-zinc-900 text-white text-sm h-9 rounded-sm">
              <Plus size={14} /> Attach vehicle
            </button>
            <div className="mt-4 space-y-2 max-h-72 overflow-y-auto">
              {attachedVehicles.length === 0 && (
                <div className="text-xs text-zinc-500">No vehicles attached.</div>
              )}
              {attachedVehicles.map((v) => (
                <div key={v.id} className="flex items-center justify-between bg-zinc-50 border border-zinc-200 rounded px-3 py-2 text-xs" data-testid={`fit-chip-${v.id}`}>
                  <span className="font-mono">{v.label}</span>
                  <button type="button" onClick={() => removeVehicle(v.id)} className="text-zinc-500 hover:text-red-600"><X size={12} /></button>
                </div>
              ))}
            </div>
          </Card>

          <button type="submit" data-testid="form-submit" disabled={saving} className="w-full h-11 bg-red-600 hover:bg-red-700 text-white text-sm font-medium tracking-wide rounded-sm disabled:opacity-50">
            {saving ? "Saving…" : isNew ? "Create product" : "Save changes"}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}

const inputCls = "w-full h-10 px-3 text-sm border border-zinc-300 focus:border-zinc-900 focus:outline-none bg-white rounded-sm";

function Card({ title, children }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-5">
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-4">{title}</div>
      {children}
    </div>
  );
}
function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1.5 block">
        {label} {required && <span className="text-red-600">*</span>}
      </label>
      {children}
    </div>
  );
}
